import { Component, computed, inject, input } from '@angular/core';
import { ApplicationService } from "../../../services/application.service";
import { AsyncPipe } from "@angular/common";
import { GalleriaModule } from "primeng/galleria";
import {
  ApartmentDetailPageContentComponent
} from "../../../components/apartment-detail-page-content/apartment-detail-page-content.component";
import { UserService } from "../../../services/user.service";
import { EstimatedEligibilityPipe } from "../../../pipes/estimated-eligibility.pipe";
import { SubmitButtonComponent } from "../../../components/submit-button/submit-button.component";
import { ApartmentRelevantAdvertisementsPipe } from "../../../pipes/apartment-relevant-advertisements.pipe";
import { ApartmentDetailInfoItemComponent } from "./apartment-detail-info-item/apartment-detail-info-item.component";
import {RegularEligibleUnitsComponent} from "../../../components/apartment-detail-page-content/eligible-units-component/regular-eligible-units.component";
import {SeniorEligibleUnitsComponent} from "../../../components/apartment-detail-page-content/eligible-units-component/senior-eligible-units.component";
import { MaxHouseholdSizePipe } from "../../../pipes/max-household-size.pipe";
import { MinHouseholdSizePipe } from "../../../pipes/min-household-size.pipe";
import { Button } from "primeng/button";
import { ModalService } from "../../../services/modal.service";
import { MessageService } from "primeng/api";
import { LotteryTimelineComponent } from "../../../components/lottery-timeline/lottery-timeline.component";
import { RouterLink } from "@angular/router";
import {ConfirmDialog} from "primeng/confirmdialog";
import {isDeferredMandatoryUserAwaitingPayment} from "../../../model/user";
import {toSignal} from "@angular/core/rxjs-interop";
import {
  AutoApplyStickyFooterComponent
} from "../../../components/auto-apply-sticky-footer/auto-apply-sticky-footer.component";
import {
  StartNextStepButtonComponent
} from "../../../components/start-next-step-button/start-next-step-button.component";

const WITHDRAW_CONFIRM_TITLE = 'Withdraw application';
const WITHDRAW_CONFIRM_BODY = 'Are you sure you want to withdraw this application? You will not be able to apply again for this specific lottery.';
const WITHDRAW_TOAST_SUMMARY = 'Application withdrawn';
const WITHDRAW_TOAST_DETAIL = 'Your application has been successfully withdrawn.';

@Component({
  standalone: true,
  imports: [
    AsyncPipe,
    GalleriaModule,
    ApartmentDetailPageContentComponent,
    EstimatedEligibilityPipe,
    SubmitButtonComponent,
    ApartmentRelevantAdvertisementsPipe,
    ApartmentDetailInfoItemComponent,
    RegularEligibleUnitsComponent,
    SeniorEligibleUnitsComponent,
    MaxHouseholdSizePipe,
    MinHouseholdSizePipe,
    Button,
    LotteryTimelineComponent,
    RouterLink,
    ConfirmDialog,
    AutoApplyStickyFooterComponent,
    StartNextStepButtonComponent,
  ],
  templateUrl: './apartment-detail-page.component.html',
  host: {class: 'fill-page'},
})
export class ApartmentDetailPageComponent {
  constructor(
    private readonly applicationService: ApplicationService,
    private readonly userService: UserService
  ) {}

  private readonly modalService = inject(ModalService);
  private readonly messageService = inject(MessageService);

  public readonly yesterday = new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString();

  private readonly userSignal = toSignal(this.userService.getCurrentUserAfterSignupFlow$());
  protected readonly deferredAwaitingPayment = computed(() => {
    const currentUser = this.userSignal();
    return !!currentUser
      && currentUser.status === 'mandatory-flow'
      && isDeferredMandatoryUserAwaitingPayment(currentUser);
  });

  public readonly lotteryId = input.required<string>();
  public readonly apartmentWithApplicationInfo = this.applicationService.getApartmentById(this.lotteryId)

  protected readonly user$ = this.userService.getCurrentUserAfterSignupFlow$();
  protected readonly encodeURIComponent = encodeURIComponent;

  public readonly apply = () =>
    this.applicationService.applyForApartment(this.apartmentWithApplicationInfo()!.id);

  protected onWithdrawClick() {
    const title = WITHDRAW_CONFIRM_TITLE;
    const body = WITHDRAW_CONFIRM_BODY;

    this.modalService.openYesNoModal(body, title, "lottery-confirm").subscribe((result) => {
      if (result === 'yes') {
        this.applicationService
          .withdrawApplication(this.apartmentWithApplicationInfo()!.application!.id!)
          .subscribe(() => {
            this.messageService.add({
              severity: 'success',
              summary: WITHDRAW_TOAST_SUMMARY,
              detail:  WITHDRAW_TOAST_DETAIL,
            });
          });
      }
    });
  }
}
